"use client";
import {
	RiAndroidLine,
	RiAppleLine,
	RiDiscordLine,
	RiDrinksLine,
	RiLifebuoyLine,
	RiLogoutBoxRLine,
	RiSettings3Line,
} from "@remixicon/react";
import {
	Avatar,
	AvatarFallback,
	AvatarImage,
} from "@workspace/ui/components/avatar";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@workspace/ui/components/dropdown-menu";
import { cn } from "@workspace/ui/lib/utils";
import { TopNavTheme } from "./top-nav-theme";

export type TopNavUser = {
	name: string;
	email: string;
	avatar: string;
	username?: string;
};

export interface TopNavProfileProps {
	user: TopNavUser;
	onSignOut?: () => void;
	onReportIssues?: () => void;
}

export function TopNavProfile({
	user,
	onSignOut,
	onReportIssues,
}: TopNavProfileProps) {
	// Check if the avatar is valid (not null, undefined, or empty string)
	const hasAvatar = !!user.avatar && user.avatar.trim() !== "";

	const handleAccountClick = () => {
		window.location.href = "/settings";
	};

	const handleProfileClick = () => {
		if (user.username) {
			window.location.href = `/${user.username}`;
		} else {
			// Fallback - should rarely happen if username is properly set
			window.location.href = "/profile";
		}
	};

	const handleReportIssuesClick = () => {
		if (onReportIssues) {
			onReportIssues();
		} else {
			const email = "support@tryhumble.com";
			const subject = encodeURIComponent("Issue Report");
			const mailtoLink = `mailto:${email}?subject=${subject}`;
			window.location.href = mailtoLink;
		}
	};

	const handleLeaveTipClick = () => {
		window.open(
			"https://buy.stripe.com/7sIcMO56o8fUd7q288",
			"_blank",
			"noopener,noreferrer",
		);
	};

	const handleDiscordJoin = () => {
		window.open("https://discord.gg/BWCeC5jVQt", "_blank");
	};

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<button
					type="button"
					className={cn(
						"relative flex h-8 w-8 items-center justify-center rounded-full outline-none transition-all duration-200",
						"hover:ring-4 hover:ring-primary/10 active:scale-95 active:ring-primary/20",
						"focus-visible:ring-2 focus-visible:ring-primary/30",
					)}
					aria-label="User menu"
				>
					<Avatar className="h-8 w-8">
						{hasAvatar && (
							<AvatarImage
								src={user.avatar}
								alt={user.name}
								className="transition-transform duration-200 hover:scale-105 active:scale-100"
							/>
						)}
						<AvatarFallback>
							{(user.username || user.name).charAt(0).toUpperCase()}
						</AvatarFallback>
					</Avatar>
				</button>
			</DropdownMenuTrigger>
			<DropdownMenuContent
				align="end"
				sideOffset={8}
				className="w-56 rounded-2xl"
			>
				<DropdownMenuLabel className="p-0 font-normal">
					<button
						type="button"
						onClick={handleProfileClick}
						className="flex w-full items-center gap-2 rounded-t-xl rounded-b-sm px-2 py-1.5 text-left text-sm transition-colors hover:bg-muted hover:text-foreground focus:bg-muted focus:text-foreground focus-visible:outline-none focus-visible:ring-0"
					>
						<Avatar className="h-8 w-8 rounded-full">
							{hasAvatar && <AvatarImage src={user.avatar} alt={user.name} />}
							<AvatarFallback className="rounded-full">
								{(user.username || user.name).charAt(0).toUpperCase()}
							</AvatarFallback>
						</Avatar>
						<div className="grid flex-1 text-left text-sm leading-tight">
							<span className="truncate font-semibold">{user.name}</span>
							<span className="truncate text-xs">{user.email}</span>
						</div>
					</button>
				</DropdownMenuLabel>
				<DropdownMenuSeparator />
				<DropdownMenuGroup>
					<TopNavTheme />
				</DropdownMenuGroup>
				<DropdownMenuSeparator />
				<DropdownMenuGroup>
					<DropdownMenuItem
						onClick={handleAccountClick}
						className="cursor-pointer rounded-sm"
					>
						<RiSettings3Line className="h-4 w-4" />
						Settings
					</DropdownMenuItem>
					<DropdownMenuItem
						onClick={handleDiscordJoin}
						className="cursor-pointer rounded-sm"
					>
						<RiDiscordLine className="h-4 w-4" />
						Join Discord
					</DropdownMenuItem>
					<DropdownMenuItem
						onClick={handleLeaveTipClick}
						className="cursor-pointer rounded-sm"
					>
						<RiDrinksLine className="h-4 w-4" />
						Buy Me Coffee
					</DropdownMenuItem>
					<DropdownMenuItem
						onClick={handleReportIssuesClick}
						className="cursor-pointer rounded-sm"
					>
						<RiLifebuoyLine className="h-4 w-4" />
						Contact Support
					</DropdownMenuItem>
				</DropdownMenuGroup>
				<DropdownMenuSeparator />
				<DropdownMenuGroup>
					<DropdownMenuItem asChild className="cursor-pointer rounded-sm">
						<a
							href="https://apps.apple.com/us/app/humble-festivals-with-friends/id6749406367"
							target="_blank"
							rel="noopener noreferrer"
						>
							<RiAppleLine className="h-4 w-4" />
							iOS
						</a>
					</DropdownMenuItem>
					<DropdownMenuItem asChild className="cursor-pointer rounded-sm">
						<a
							href="https://play.google.com/store/apps/details?id=com.humbleos.mobile&hl=en_US"
							target="_blank"
							rel="noopener noreferrer"
						>
							<RiAndroidLine className="h-4 w-4" />
							Android
						</a>
					</DropdownMenuItem>
				</DropdownMenuGroup>
				<DropdownMenuSeparator />
				<DropdownMenuItem
					onClick={onSignOut}
					className="hover:!text-radix-red-9 cursor-pointer rounded-sm rounded-b-xl text-radix-red-9 hover:bg-radix-red-4"
				>
					<RiLogoutBoxRLine className="h-4 w-4" />
					Sign Out
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
